import { useTheme } from 'next-themes';
import { useMounted } from '@/hooks/mounted.hook';
import { cn } from '@/utils/classnames';

interface ThemeToggleProps {
  className?: string;
  size?: number;
}

export default function ThemeToggle({ className, size = 44 }: ThemeToggleProps) {
  const mounted = useMounted();
  const { theme, setTheme } = useTheme();

  if (!mounted) {
    return (
      <div
        className={cn(
          'rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center',
          className,
        )}
        style={{ width: size, height: size }}
        suppressHydrationWarning
      >
        <div style={{ width: size * 0.45, height: size * 0.45 }}></div>
      </div>
    );
  }

  const isDark = theme === 'dark';

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'rounded-full transition-all duration-200 flex items-center justify-center group',
        className,
      )}
      style={{ width: size, height: size }}
      suppressHydrationWarning
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        // Sun icon for light mode
        <svg
          className="text-white group-hover:text-gray-300 transition-colors duration-200"
          style={{ width: size, height: size }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 12 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 2V1M6 11V10M10 6H11M1 6H2M8.8285 3.1715L9.535 2.465M2.465 9.535L3.172 8.828M3.172 3.171L2.465 2.465M9.535 9.535L8.828 8.828M6 8.5C6.66304 8.5 7.29893 8.23661 7.76777 7.76777C8.23661 7.29893 8.5 6.66304 8.5 6C8.5 5.33696 8.23661 4.70107 7.76777 4.23223C7.29893 3.76339 6.66304 3.5 6 3.5C5.33696 3.5 4.70107 3.76339 4.23223 4.23223C3.76339 4.70107 3.5 5.33696 3.5 6C3.5 6.66304 3.76339 7.29893 4.23223 7.76777C4.70107 8.23661 5.33696 8.5 6 8.5Z"
            strokeWidth="0.872727"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        // Moon icon for dark mode
        <svg
          viewBox="0 0 11 11"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          className="text-black group-hover:text-gray-600 transition-colors duration-200"
          style={{ width: size - 3, height: size - 3 }}
        >
          <path d="M10.2674 5.75C10.2674 6.9481 9.7915 8.09713 8.94431 8.94431C8.09713 9.7915 6.9481 10.2674 5.75 10.2674V11C8.64946 11 11 8.64946 11 5.75H10.2674ZM5.75 10.2674C4.5519 10.2674 3.40287 9.7915 2.55569 8.94431C1.7085 8.09713 1.23256 6.9481 1.23256 5.75H0.5C0.5 8.64946 2.85053 11 5.75 11V10.2674ZM1.23256 5.75C1.23256 4.5519 1.7085 3.40287 2.55569 2.55569C3.40287 1.7085 4.5519 1.23256 5.75 1.23256V0.5C2.85053 0.5 0.5 2.85053 0.5 5.75H1.23256ZM7.4593 6.84884C6.71454 6.84884 6.00028 6.55298 5.47365 6.02635C4.94702 5.49972 4.65116 4.78546 4.65116 4.0407H3.9186C3.9186 4.97975 4.29164 5.88034 4.95565 6.54435C5.61966 7.20836 6.52025 7.5814 7.4593 7.5814V6.84884ZM9.86453 5.49067C9.61464 5.90513 9.26187 6.24797 8.84045 6.48593C8.41903 6.7239 7.94327 6.84891 7.4593 6.84884V7.5814C8.06947 7.58158 8.66932 7.42407 9.20069 7.12416C9.73206 6.82425 10.1769 6.39211 10.4921 5.86965L9.86453 5.49067ZM4.65116 4.0407C4.65109 3.55673 4.7761 3.08097 5.01407 2.65955C5.25203 2.23813 5.59487 1.88536 6.00933 1.63547L5.63035 1.0084C5.10796 1.32353 4.67587 1.76831 4.37596 2.29958C4.07605 2.83086 3.91851 3.43062 3.9186 4.0407H4.65116ZM5.75 1.23256C5.72546 1.23209 5.70125 1.22675 5.67878 1.21686C5.65632 1.20697 5.63604 1.19271 5.61912 1.17493C5.59853 1.15425 5.58458 1.12789 5.57907 1.09923C5.57712 1.08409 5.57809 1.04014 5.63035 1.0084L6.00933 1.63547C6.25498 1.487 6.33409 1.21498 6.30528 1.00107C6.275 0.77886 6.10016 0.5 5.75 0.5V1.23256ZM10.4921 5.86965C10.4599 5.92191 10.4159 5.92288 10.4008 5.92093C10.3721 5.91542 10.3458 5.90147 10.3251 5.88088C10.3073 5.86396 10.293 5.84368 10.2831 5.82122C10.2733 5.79875 10.2679 5.77454 10.2674 5.75H11C11 5.39984 10.7211 5.225 10.4989 5.19472C10.285 5.16591 10.013 5.24502 9.86453 5.49067L10.4921 5.86965Z" />
        </svg>
      )}
    </button>
  );
}
